import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Printer, Loader2, X } from "lucide-react";
import { useLang } from "../lib/i18n";

/** In-app PDF preview with print + download, so a report can be checked before printing. */
export function PdfPreview({ open, onClose, blob, title, filename, loading }) {
  const { t } = useLang();
  const [url, setUrl] = useState("");
  const frame = useRef(null);

  useEffect(() => {
    if (!blob) { setUrl(""); return; }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const print = () => {
    try { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print(); }
    catch { window.open(url, "_blank")?.print(); }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = filename || "rapport.pdf"; a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] h-[92vh] p-0 flex flex-col gap-0 overflow-hidden" data-testid="pdf-preview">
        <DialogHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-sm font-semibold text-slate-900 truncate pr-4" data-testid="pdf-preview-title">{title || t("Aperçu du rapport")}</DialogTitle>
          <div className="flex items-center gap-2 shrink-0 mr-6">
            <Button size="sm" variant="outline" onClick={download} disabled={!url} data-testid="pdf-preview-download">
              <Download className="h-4 w-4 mr-1.5" />{t("Télécharger")}
            </Button>
            <Button size="sm" className="bg-[#0F172A] hover:bg-[#1E293B]" onClick={print} disabled={!url} data-testid="pdf-preview-print">
              <Printer className="h-4 w-4 mr-1.5" />{t("Imprimer")}
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 min-h-0">
          {loading || !url ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-[#F05A28]" />{t("Préparation du PDF…")}
            </div>
          ) : (
            <iframe ref={frame} src={`${url}#toolbar=1&view=FitH`} title="pdf" className="w-full h-full border-0" data-testid="pdf-preview-frame" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small hook: fetch a PDF blob and open it in the preview. */
export function usePdfPreview() {
  const [state, setState] = useState({ open: false, blob: null, title: "", filename: "", loading: false });
  const show = async (fetcher, { title, filename } = {}) => {
    setState({ open: true, blob: null, title, filename, loading: true });
    try {
      const blob = await fetcher();
      setState({ open: true, blob, title, filename, loading: false });
      return blob;
    } catch (e) {
      setState((s) => ({ ...s, open: false, loading: false }));
      throw e;
    }
  };
  const close = () => setState((s) => ({ ...s, open: false }));
  const el = <PdfPreview open={state.open} onClose={close} blob={state.blob} title={state.title} filename={state.filename} loading={state.loading} />;
  return { show, close, el, X };
}

export default PdfPreview;
